import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Shared submissions table. Renders the audit-log rows for BOTH the admin-wide
 * view (`AdminSubmissionsPage`, `showUser` on) and the per-user self view
 * (`MySubmissionsPage`, `showUser` off). Keeping the row markup + result
 * formatting here means the two pages never drift apart.
 *
 * The parent owns fetching + filtering; this component only renders `items`
 * and manages its own per-row "Show JSON" expand state.
 */

export type SubmissionKind = 'venue' | 'series' | 'ingest' | 'compose';
export type SubmissionStatus = 'success' | 'error';

export interface Submission {
  partitionKey: string;
  rowKey: string;
  kind: SubmissionKind;
  status: SubmissionStatus;
  createdAt: string;
  capturedByUser?: string;
  inputUrl?: string;
  inputName?: string;
  inputType?: string;
  inputHint?: string;
  resultJson?: string;
  errorMessage?: string;
}

export interface SubmissionsResponse {
  items: Submission[];
  total: number;
  limit: number;
  monthsBack: number;
}

const UNKNOWN_KIND_META = { label: 'Unknown', badge: 'bg-secondary' } as const;
const UNKNOWN_STATUS_META = { label: 'Unknown', badge: 'bg-secondary' } as const;

const KIND_META: Record<SubmissionKind, { label: string; badge: string }> = {
  venue: { label: 'Venue', badge: 'bg-info text-dark' },
  series: { label: 'Series', badge: 'bg-primary' },
  ingest: { label: 'Event / file', badge: 'bg-secondary' },
  compose: { label: 'Composed event', badge: 'bg-success' }
};

const STATUS_META: Record<SubmissionStatus, { label: string; badge: string }> = {
  success: { label: 'Success', badge: 'bg-success' },
  error: { label: 'Error', badge: 'bg-danger' }
};

function formatRelative(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const diffMs = Date.now() - d.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString();
}

function formatAbsolute(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

interface ParsedResult {
  venueId?: string;
  seriesId?: string;
  ingestId?: string;
  name?: string;
  created?: boolean;
  // Website scan
  websiteScanIngested?: number;
  websiteScanError?: string;
  websiteScanSkippedReason?: string;
  // Instagram scan
  igScanIngested?: number;
  igScanError?: string;
  igScanSkippedReason?: string;
  // Facebook scan
  fbScanIngested?: number;
  fbScanError?: string;
  fbScanSkippedReason?: string;
  // Misc
  detected?: string[];
  instagramHandle?: string;
  facebookUrl?: string;
  website?: string;
}

function parseResult(json?: string): ParsedResult | undefined {
  if (!json) return undefined;
  try { return JSON.parse(json) as ParsedResult; }
  catch { return undefined; }
}

function detailLinkForResult(kind: SubmissionKind, parsed?: ParsedResult): { to: string; label: string } | null {
  if (!parsed) return null;
  if (kind === 'venue' && parsed.venueId) {
    return { to: `/venues/${encodeURIComponent(parsed.venueId)}`, label: 'View venue' };
  }
  if (kind === 'series' && parsed.seriesId) {
    return { to: `/series/${encodeURIComponent(parsed.seriesId)}`, label: 'View series' };
  }
  return null;
}

function prettyJson(json?: string): string | undefined {
  if (!json) return undefined;
  try { return JSON.stringify(JSON.parse(json), null, 2); }
  catch { return json; }
}

function ResultSummary({ kind, parsed }: { kind: SubmissionKind; parsed: ParsedResult }) {
  if (kind === 'venue') {
    return (
      <div>
        {parsed.created ? <span className="text-success">Created</span> : <span className="text-muted">Matched</span>}
        {parsed.name ? <> — {parsed.name}</> : null}
        {parsed.instagramHandle && (
          <div className="text-muted">@{parsed.instagramHandle}</div>
        )}
        <ScanLine label="Website" ingested={parsed.websiteScanIngested} error={parsed.websiteScanError} skipped={parsed.websiteScanSkippedReason} />
        <ScanLine label="Instagram" ingested={parsed.igScanIngested} error={parsed.igScanError} skipped={parsed.igScanSkippedReason} />
        <ScanLine label="Facebook" ingested={parsed.fbScanIngested} error={parsed.fbScanError} skipped={parsed.fbScanSkippedReason} />
      </div>
    );
  }
  if (kind === 'series') {
    return (
      <div>
        {parsed.created ? <span className="text-success">Created</span> : <span className="text-muted">Matched</span>}
        {parsed.name ? <> — {parsed.name}</> : null}
      </div>
    );
  }
  return (
    <div>
      {parsed.ingestId && (
        <div className="text-muted">Raw id: <code>{parsed.ingestId}</code></div>
      )}
      {parsed.detected && parsed.detected.length > 0 && (
        <div className="text-muted">Detected: {parsed.detected.join(', ')}</div>
      )}
    </div>
  );
}

function ScanLine({ label, ingested, error, skipped }: { label: string; ingested?: number; error?: string; skipped?: string }) {
  if (typeof ingested === 'number') {
    return (
      <div className="text-muted">
        {label} scan: {ingested} ingested{error ? <span className="text-danger"> — {error}</span> : null}
      </div>
    );
  }
  if (error) {
    return <div className="text-danger">{label} scan error: {error}</div>;
  }
  if (skipped) {
    return <div className="text-muted">{label} scan skipped: {skipped}</div>;
  }
  return null;
}

interface SubmissionsTableProps {
  items: Submission[];
  /** When true, render the "By" column (admin cross-user view). */
  showUser: boolean;
}

export default function SubmissionsTable({ items, showUser }: SubmissionsTableProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggleExpanded = (key: string) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  const colSpan = showUser ? 7 : 6;

  return (
    <div className="table-responsive">
      <table className="table table-sm table-hover align-middle">
        <thead>
          <tr>
            <th scope="col">When</th>
            <th scope="col">Kind</th>
            <th scope="col">Status</th>
            <th scope="col">Input</th>
            <th scope="col">Result</th>
            {showUser && <th scope="col">By</th>}
            <th scope="col" className="text-end">Details</th>
          </tr>
        </thead>
        <tbody>
          {items.map((s) => {
            const key = `${s.partitionKey}|${s.rowKey}`;
            const parsed = parseResult(s.resultJson);
            const link = detailLinkForResult(s.kind, parsed);
            const isOpen = !!expanded[key];
            return (
              <Fragment key={key}>
                <tr>
                  <td>
                    <span title={formatAbsolute(s.createdAt)} className="small">
                      {formatRelative(s.createdAt)}
                    </span>
                  </td>
                  <td>
                    {(() => {
                      const meta = KIND_META[s.kind] ?? UNKNOWN_KIND_META;
                      return (
                        <span className={`badge ${meta.badge}`}>
                          {meta.label}
                        </span>
                      );
                    })()}
                    {s.inputType && (
                      <div className="small text-muted">{s.inputType}</div>
                    )}
                  </td>
                  <td>
                    {(() => {
                      const meta = STATUS_META[s.status] ?? UNKNOWN_STATUS_META;
                      return (
                        <span className={`badge ${meta.badge}`}>
                          {meta.label}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="small" style={{ maxWidth: 360 }}>
                    {s.inputUrl && (
                      <div className="text-truncate" title={s.inputUrl}>
                        <a href={s.inputUrl} target="_blank" rel="noopener noreferrer">
                          {s.inputUrl}
                        </a>
                      </div>
                    )}
                    {s.inputName && (
                      <div className="text-muted text-truncate" title={s.inputName}>
                        {s.inputName}
                      </div>
                    )}
                    {s.inputHint && (
                      <div className="text-muted text-truncate" title={s.inputHint}>
                        <em>hint:</em> {s.inputHint}
                      </div>
                    )}
                  </td>
                  <td className="small" style={{ maxWidth: 360 }}>
                    {s.status === 'error' && s.errorMessage && (
                      <span className="text-danger">{s.errorMessage}</span>
                    )}
                    {s.status === 'success' && parsed && (
                      <ResultSummary kind={s.kind} parsed={parsed} />
                    )}
                  </td>
                  {showUser && <td className="small text-muted">{s.capturedByUser || ''}</td>}
                  <td className="text-end small">
                    {link && (
                      <Link to={link.to} className="me-2">{link.label}</Link>
                    )}
                    {(s.resultJson || s.errorMessage) && (
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        onClick={() => toggleExpanded(key)}
                        aria-expanded={isOpen ? 'true' : 'false'}
                      >
                        {isOpen ? 'Hide JSON' : 'Show JSON'}
                      </button>
                    )}
                  </td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={colSpan}>
                      <pre className="mb-0 small bg-body-tertiary p-2 rounded" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {prettyJson(s.resultJson) ?? s.errorMessage ?? ''}
                      </pre>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
